import { Bell, BellOff, BellRing } from 'lucide-react'
import { useEffect, useState } from 'react'

import { usePushConfig } from '../api/client'
import { de } from '../i18n/de'
import { currentState, disable, enable, type PushState } from '../lib/push'

/**
 * Switches browser notifications for this browser on or off.
 *
 * Per browser, not per account: permission is something a browser grants, so signing in on
 * a second device means allowing it there too. Hidden entirely when the server has no VAPID
 * keys — a button that cannot work is worse than no button.
 */
export function NotificationToggle({ variant = 'icon' }: { variant?: 'icon' | 'menu' }) {
  const { data: config } = usePushConfig()
  const [state, setState] = useState<PushState>('off')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!config) return
    void currentState(config.enabled).then(setState)
  }, [config])

  if (!config?.enabled || state === 'unsupported' || state === 'disabled') return null

  const label =
    state === 'on'
      ? de.push.on
      : state === 'denied'
        ? de.push.blocked
        : de.push.off

  const toggle = async () => {
    if (state === 'denied' || busy) return
    setBusy(true)
    setError(null)
    try {
      setState(state === 'on' ? await disable() : await enable(config.public_key!))
    } catch (problem) {
      setError((problem as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const Icon = state === 'on' ? BellRing : state === 'denied' ? BellOff : Bell

  if (variant === 'menu') {
    return (
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={busy || state === 'denied'}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-slate-700 hover:bg-slate-100 disabled:opacity-60 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy || state === 'denied'}
      title={error ?? (state === 'denied' ? de.push.blockedHint : label)}
      aria-label={label}
      className={`rounded-lg p-1.5 transition hover:bg-slate-100 disabled:opacity-60 dark:hover:bg-slate-800 ${
        state === 'on'
          ? 'text-indigo-600 dark:text-indigo-400'
          : 'text-slate-500 dark:text-slate-400'
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  )
}
